import { supabase } from "@/integrations/supabase/client";

const KEY = "pendingRsvpEventId";

/** Запомнить, что человек хотел записаться на конкретное мероприятие. */
export function setPendingRsvp(eventId: string): void {
  if (typeof window === "undefined" || !eventId) return;
  try {
    window.localStorage.setItem(KEY, eventId);
  } catch {
    /* ignore */
  }
}

export function getPendingRsvp(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function clearPendingRsvp(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Оформить запись на мероприятие (идемпотентно).
 * Возвращает true, если после вызова человек записан.
 */
export async function ensureRsvp(eventId: string, userId: string): Promise<boolean> {
  const { data: existing } = await supabase
    .from("event_rsvps")
    .select("event_id")
    .eq("event_id", eventId)
    .eq("user_id", userId)
    .maybeSingle();

  if (existing) return true;

  const { error } = await supabase
    .from("event_rsvps")
    .insert({ event_id: eventId, user_id: userId });
  if (error) return false;

  try {
    void supabase.functions.invoke("send-event-rsvp-confirmation", {
      body: { eventId, userId },
    });
  } catch (e) {
    console.error("Не удалось отправить подтверждение записи", e);
  }
  return true;
}

/**
 * Незаметно дописывает код сверки в предзаполненное сообщение WhatsApp.
 * Пользователю код нигде не показывается — он нужен только организатору.
 */
export function appendCodeToWhatsappMessage(ticketUrl: string, code: string | null): string {
  if (!code) return ticketUrl;
  try {
    const url = new URL(ticketUrl);
    const current = url.searchParams.get("text");
    if (current === null) return ticketUrl;
    url.searchParams.set("text", `${current} (код: ${code})`);
    return url.toString();
  } catch {
    return ticketUrl;
  }
}

/** Короткий человекочитаемый код записи — для сверки оплаты. */
export function purchaseCode(rsvpId: string): string {
  return rsvpId.slice(-6).toUpperCase();
}

/**
 * Зафиксировать намерение купить билет на платное мероприятие (идемпотентно).
 * Возвращает короткий код записи или null.
 */
export async function createPendingPurchase(
  eventId: string,
  userId: string,
): Promise<string | null> {
  const { data: existing } = await supabase
    .from("event_rsvps")
    .select("id")
    .eq("event_id", eventId)
    .eq("user_id", userId)
    .maybeSingle();

  if (existing) return purchaseCode(existing.id);

  const { data, error } = await supabase
    .from("event_rsvps")
    .insert({ event_id: eventId, user_id: userId, status: "pending_payment" })
    .select("id")
    .maybeSingle();
  if (error || !data) return null;
  return purchaseCode(data.id);
}
